import React, { useMemo, useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Card, CardContent, CardHeader, IconButton } from '@mui/material';

import { ComponentPreview, MenuAnchor } from '@paragon/client-tools';
import { NumericField } from '@paragon/material';
import { calculateMemberGeometry } from '@paragon/relative-geometry';
import { notNullish } from '@paragon/utilities';

import { getComponentDesignNames } from '../../../selectors/componentDesignSelectors';
import { RootState } from '../../../store';
import { useAppSelector } from '../../hooks';
import { ComponentDesignMenu } from '../menus/ComponentDesignMenu';

interface ComponentDesignCardProps {
  readonly interactive: boolean;

  readonly context: 'station' | 'assembly group';
  readonly componentDesignGuid: string;
  readonly quantity: number | null;

  readonly onUpdateComponentDesignQuantity: (quantity: number) => void;
  readonly onRemoveComponentDesign: () => void;
}

export function ComponentDesignCard(props: ComponentDesignCardProps) {
  const {
    interactive,
    context,
    componentDesignGuid,
    quantity,
    onUpdateComponentDesignQuantity,
    onRemoveComponentDesign,
  } = props;

  const [menuAnchor, setMenuAnchor] = useState<MenuAnchor>(null);

  const componentDesign = useAppSelector((state: RootState) => state.componentDesigns.data[componentDesignGuid]);
  const memberVertices = useMemo(
    () =>
      componentDesign
        ? componentDesign.component.members
            .map((member) => calculateMemberGeometry(member, componentDesign.component))
            .filter(notNullish)
        : null,
    [componentDesign],
  );

  const componentDesignNames = useAppSelector(getComponentDesignNames);

  const handleContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    if (interactive) {
      setMenuAnchor({ x: event.clientX, y: event.clientY });
    }
  };

  return (
    <>
      {interactive && (
        <ComponentDesignMenu
          context={context}
          componentDesign={componentDesign}
          anchor={menuAnchor}
          onClose={() => setMenuAnchor(null)}
          onRemoveComponentDesign={onRemoveComponentDesign}
        />
      )}

      <Card onContextMenu={handleContextMenu}>
        <CardHeader
          title={componentDesignNames[componentDesignGuid] ?? 'unknown'}
          action={
            <>
              <IconButton
                onClick={(event) => {
                  event.stopPropagation();
                  onRemoveComponentDesign();
                }}
                disabled={!interactive}
              >
                <DeleteIcon />
              </IconButton>
            </>
          }
        ></CardHeader>
        <CardContent>
          {memberVertices && (
            <Box sx={{ pt: 0, display: 'flex', justifyContent: 'center' }}>
              <ComponentPreview maxWidth={300} maxHeight={100} memberVertices={memberVertices} />
            </Box>
          )}
          <NumericField
            sx={{ mt: 4 }}
            value={quantity ?? 1}
            label="Quantity"
            integers
            min={1}
            disabled={!interactive}
            onSubmit={(value) => void onUpdateComponentDesignQuantity(value)}
          />
        </CardContent>
      </Card>
    </>
  );
}
